import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '../lib/api';

type RangeKey = '7d' | '30d' | '90d';

type SummaryResponse = {
  transactionCount: number;
  totalSalesCents: number;
  averageTransactionCents: number;
  tradeCount: number;
  tradeValueCents: number;
  tradeItemsQty: number;
};

type PriceKpisResponse = {
  avgSellPriceCents: number;
  avgMarketPriceCents: number;
  pricedSkuCount: number;
};

type CardsByGameResponse = {
  points: Array<{
    game: string;
    products: number;
    qtyOnHand: number;
  }>;
};

type SalesSeriesResponse = {
  points: Array<{
    day: string;
    transactions: number;
    totalSalesCents: number;
  }>;
};

type TradeinSeriesResponse = {
  points: Array<{
    day: string;
    tradeCount: number;
    tradeValueCents: number;
    itemsQty: number;
  }>;
};

type Mover = {
  skuId: string;
  name: string;
  setName: string | null;
  game: string | null;
  printing: string | null;
  marketCents: number | null;
  priceChangePercent: number | null;
};

type PriceMoversResponse = {
  gainers: Mover[];
  losers: Mover[];
};

const PIE_COLORS = ['#10b981', '#22d3ee', '#60a5fa', '#f59e0b', '#f43f5e', '#a78bfa'];

function formatMoney(cents: number | null | undefined): string {
  return `$${((cents ?? 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function toDateRange(range: RangeKey): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime());
  const days = range === '7d' ? 7 : range === '90d' ? 90 : 30;
  from.setDate(from.getDate() - days);
  return { from: from.toISOString(), to: to.toISOString() };
}

function shortDay(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  if (!Number.isFinite(d.getTime())) return day;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function AnalyticsPage() {
  const [range, setRange] = useState<RangeKey>('30d');
  const { from, to } = useMemo(() => toDateRange(range), [range]);

  const summary = useQuery({
    queryKey: ['analytics', 'summary', range],
    queryFn: () => api.get<SummaryResponse>(`/analytics/summary?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  });

  const priceKpis = useQuery({
    queryKey: ['analytics', 'price-kpis', range],
    queryFn: () => api.get<PriceKpisResponse>(`/analytics/price-kpis?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  });

  const cardsByGame = useQuery({
    queryKey: ['analytics', 'cards-by-game', range],
    queryFn: () => api.get<CardsByGameResponse>(`/analytics/cards-by-game?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  });

  const salesSeries = useQuery({
    queryKey: ['analytics', 'sales-series', range],
    queryFn: () => api.get<SalesSeriesResponse>(`/analytics/sales-series?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  });

  const tradeinSeries = useQuery({
    queryKey: ['analytics', 'tradein-series', range],
    queryFn: () => api.get<TradeinSeriesResponse>(`/analytics/tradein-series?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  });

  const movers = useQuery({
    queryKey: ['analytics', 'price-movers', range],
    queryFn: () => api.get<PriceMoversResponse>('/analytics/price-movers?limit=8&sinceDays=7'),
    retry: false,
  });

  const marketMoversError = movers.error instanceof Error ? movers.error.message : null;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Analytics</h1>
          <p className="text-sm text-ink-muted">Sales, trade-ins, card mix, and 7-day market movers.</p>
        </div>
        <label className="text-xs text-ink-muted">
          <span className="block mb-1">Range</span>
          <select
            className="bg-card border border-border rounded-lg px-3 py-2"
            value={range}
            onChange={(e) => setRange(e.target.value as RangeKey)}
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </label>
      </header>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <KpiCard
          label="Recorded sales"
          value={formatMoney(summary.data?.totalSalesCents)}
          loading={summary.isLoading}
        />
        <KpiCard
          label="Transactions"
          value={(summary.data?.transactionCount ?? 0).toLocaleString()}
          loading={summary.isLoading}
        />
        <KpiCard
          label="Avg ticket"
          value={formatMoney(summary.data?.averageTransactionCents)}
          loading={summary.isLoading}
        />
        <KpiCard
          label="Trade-ins"
          value={(summary.data?.tradeCount ?? 0).toLocaleString()}
          loading={summary.isLoading}
        />
        <KpiCard
          label="Trade-in value"
          value={formatMoney(summary.data?.tradeValueCents)}
          loading={summary.isLoading}
        />
        <KpiCard
          label="Avg market price"
          value={formatMoney(priceKpis.data?.avgMarketPriceCents)}
          loading={priceKpis.isLoading}
        />
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <Panel title="Cards by game (in stock)">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={(cardsByGame.data?.points ?? []).map((p) => ({ name: p.game, value: p.qtyOnHand }))}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={100}
                  label
                >
                  {(cardsByGame.data?.points ?? []).map((point, idx) => (
                    <Cell key={`${point.game}:${idx}`} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Sales trend">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={(salesSeries.data?.points ?? []).map((p) => ({ ...p, label: shortDay(p.day) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="label" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === 'totalSalesCents') return formatMoney(Number(value));
                    return String(value);
                  }}
                />
                <Line type="monotone" dataKey="totalSalesCents" stroke="#10b981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Trade-in volume trend">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(tradeinSeries.data?.points ?? []).map((p) => ({ ...p, label: shortDay(p.day) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="label" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip formatter={(value, name) => (name === 'tradeValueCents' ? formatMoney(Number(value)) : String(value))} />
                <Bar dataKey="itemsQty" fill="#60a5fa" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="7-day market movers">
          {movers.isError ? (
            <p className="text-sm text-amber-300">
              Market movers unavailable right now. {marketMoversError ?? 'Please retry in a moment.'}
            </p>
          ) : movers.data && movers.data.gainers.length === 0 && movers.data.losers.length === 0 ? (
            <p className="text-sm text-ink-muted">
              Not enough price history yet. Movers appear once SKUs have at least a week of price
              snapshots.
            </p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              <MoverList title="Top gainers" rows={movers.data?.gainers ?? []} positive />
              <MoverList title="Top losers" rows={movers.data?.losers ?? []} positive={false} />
            </div>
          )}
        </Panel>
      </section>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <article className="rounded-xl border border-track bg-card p-4 space-y-3">
      <h2 className="text-sm uppercase tracking-wide text-ink-muted">{title}</h2>
      {children}
    </article>
  );
}

function KpiCard({ label, value, loading }: { label: string; value: string; loading: boolean }) {
  return (
    <article className="rounded-xl border border-track bg-card p-3">
      <p className="text-xs text-ink-muted uppercase tracking-wide">{label}</p>
      <p className="mt-2 text-2xl font-black text-brand">{loading ? '…' : value}</p>
    </article>
  );
}

function MoverList({
  title,
  rows,
  positive,
}: {
  title: string;
  rows: Mover[];
  positive: boolean;
}) {
  return (
    <div className="rounded-lg border border-track bg-navy p-3">
      <p className="text-xs text-ink-muted uppercase tracking-wide mb-2">{title}</p>
      <ul className="space-y-2">
        {rows.length === 0 && <li className="text-xs text-ink-dim">No data.</li>}
        {rows.map((row) => (
          <li key={row.skuId} className="flex items-center justify-between gap-2 text-xs">
            <span className="truncate text-ink">{row.name}</span>
            <span className={positive ? 'text-brand font-semibold' : 'text-rose-300 font-semibold'}>
              {typeof row.priceChangePercent === 'number'
                ? `${positive ? '+' : ''}${row.priceChangePercent.toFixed(2)}%`
                : '—'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
